import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import ptBR from '@/content/pt-br';
import es from '@/content/es';
import { ROUTE_SLUGS } from '@/i18n/routes';

const API = process.env.REACT_APP_BACKEND_URL;

const LangCtx = createContext(null);
const OverrideCtx = createContext(null);

const BASE = { 'pt-br': ptBR, es };

function deepSet(obj, path, value) {
  const keys = path.split('.');
  let node = obj;
  for (let i = 0; i < keys.length - 1; i++) {
    if (typeof node[keys[i]] !== 'object' || node[keys[i]] === null) return;
    node = node[keys[i]];
  }
  if (keys[keys.length - 1] in node) node[keys[keys.length - 1]] = value;
}

export function LanguageRoot({ children }) {
  const [overrides, setOverrides] = useState({});
  const [country, setCountryState] = useState(() => localStorage.getItem('rt_country') || 'py');

  useEffect(() => {
    fetch(`${API}/api/content/all`)
      .then((r) => r.json())
      .then((d) => { if (d && typeof d === 'object') setOverrides(d); })
      .catch(() => {});
  }, []);

  // Geolocalização por IP (silenciosa): ajusta o país na versão LATAM
  useEffect(() => {
    const run = async () => {
      try {
        let cc = localStorage.getItem('rt_geo');
        if (!cc) {
          const r = await fetch('https://ipwho.is/');
          const d = await r.json();
          cc = d && d.country_code ? d.country_code : '';
          if (cc) localStorage.setItem('rt_geo', cc);
        }
        if (cc === 'AR') setCountryState('ar');
        else if (cc === 'PY') setCountryState('py');
      } catch (e) { /* fallback: Paraguai */ }
    };
    run();
  }, []);

  const setCountry = (c) => {
    setCountryState(c);
    localStorage.setItem('rt_country', c);
  };

  return (
    <OverrideCtx.Provider value={{ overrides, country, setCountry }}>
      {children}
    </OverrideCtx.Provider>
  );
}

export function VersionScope({ lang, children }) {
  const { overrides, country, setCountry } = useContext(OverrideCtx);

  const value = useMemo(() => {
    const base = BASE[lang] || BASE['pt-br'];
    const content = JSON.parse(JSON.stringify(base));
    const ov = overrides[lang] || {};
    Object.keys(ov).forEach((k) => deepSet(content, k, ov[k]));

    let countryData = null;
    if (lang === 'es' && content.byCountry) {
      countryData = content.byCountry[country] || content.byCountry.py;
      content.country = countryData;
    }

    const digits = (s) => (s || '').replace(/\D/g, '');

    const waLink = () => {
      const number = digits(countryData && countryData.whatsapp) || digits(content.whatsapp && content.whatsapp.number) || '554930251100';
      const msg = (content.whatsapp && content.whatsapp.message) || '';
      return `https://wa.me/${number}?text=${encodeURIComponent(msg)}`;
    };

    return {
      lang,
      content,
      country,
      setCountry,
      countryData,
      waLink,
      route: (key) => {
        const slugs = ROUTE_SLUGS[lang];
        return `/${lang}${slugs[key] ? '/' + slugs[key] : ''}`;
      },
      editables: content._editable || [],
    };
  }, [lang, overrides, country]);

  return <LangCtx.Provider value={value}>{children}</LangCtx.Provider>;
}

export function useLang() {
  const v = useContext(LangCtx);
  if (!v) throw new Error('useLang fora de VersionScope');
  return v;
}

export function useSeo(title, description) {
  const { lang, content } = useLang();
  useEffect(() => {
    document.documentElement.lang = content.meta && content.meta.htmlLang ? content.meta.htmlLang : lang;
    if (title) document.title = title;
    else if (content.meta && content.meta.title) document.title = content.meta.title;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc && description) metaDesc.setAttribute('content', description);
  }, [title, description, lang, content]);
}